import { addDays, type DateKey } from '@kff/engine';
import { texts, type Lang } from './i18n.js';

/**
 * Smart filters for the Projects list: free text plus stage, client, person, deadline and who the project waits on.
 * Every filter lives in the URL (?q=…&stage=…) so a filtered list can be bookmarked or sent to a colleague.
 */

export interface FilterableProject {
  code: string;
  name: string;
  client: { id: string; name: string };
  status: string;
  stage: { stage: string; label: string };
  next: { phrase: string; mine: boolean; waitingOn?: string };
  nextDeadline: string | null;
  producer: string | null;
  people?: { id: string; name: string; role: string }[];
  fromMonday?: boolean;
  /** a video here is in the viewer's "Din tur" (older API versions leave it out: the next step then decides) */
  myTurn?: boolean;
  /** hidden from the viewer's own My work: only under "Hidden" */
  hidden?: boolean;
  /** the viewer's roles on the project, and the videos they edit, for "My role" */
  myRoleKeys?: string[];
  videoList?: { mine: boolean }[];
}

export type DueFilter = '' | 'overdue' | '7d' | '30d' | 'none';
export type WaitFilter = '' | 'turn' | 'me' | 'customer' | 'editor' | 'cd' | 'producer';
/** "My role" (owner 2026-11-02, for everyone 2026-11-03): the projects where I am producer, edit, or film. */
export type MyRole = '' | 'producer' | 'editor' | 'camera';
export type SourceFilter = '' | 'monday' | 'hub';

export interface ProjectFilters {
  q: string;
  stage: string;
  client: string;
  person: string;
  due: DueFilter;
  wait: WaitFilter;
  /** Where the project was created: imported from Monday or made in the Hub. */
  src: SourceFilter;
  /** Delivery months to load (YYYY-MM); empty = the API's default window. */
  from: string;
  to: string;
  /** Show delivered, closed and cancelled projects too. */
  done: boolean;
  /** My role: only the projects where I have it */
  role: MyRole;
  /** The projects hidden from my My work, instead of the others */
  hidden: boolean;
}

export const NO_FILTERS: ProjectFilters = { q: '', stage: '', client: '', person: '', due: '', wait: '', src: '', from: '', to: '', done: false, role: '', hidden: false };

const DUE: DueFilter[] = ['overdue', '7d', '30d', 'none'];
const WAIT: WaitFilter[] = ['turn', 'me', 'customer', 'editor', 'cd', 'producer'];
const SRC: SourceFilter[] = ['monday', 'hub'];
const ROLES: MyRole[] = ['producer', 'editor', 'camera'];
const KEYS = ['q', 'stage', 'client', 'person', 'due', 'wait', 'src', 'from', 'to', 'role'] as const;
const month = (v: string | null) => (v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : '');

export const STAGE_OPTIONS: [string, string][] = [
  ['planning', 'Planning'],
  ['editing', 'Editing'],
  ['internal_review', 'Internal review'],
  ['with_customer', 'With client'],
  ['ready_to_deliver', 'Ready to deliver'],
  ['delivered', 'Delivered'],
  ['cancelled', 'Cancelled']
];
export const DUE_LABEL: Record<Exclude<DueFilter, ''>, string> = { overdue: 'Overdue', '7d': 'Next 7 days', '30d': 'Next 30 days', none: 'No deadline' };
export const SOURCE_LABEL: Record<Exclude<SourceFilter, ''>, string> = { monday: 'From Monday', hub: 'Made in the Hub' };
export const WAIT_LABEL: Record<Exclude<WaitFilter, ''>, string> = { turn: 'My turn', me: 'Me', customer: 'Client', editor: 'Editor', cd: 'CD', producer: 'Producer' };

/** Delivered, closed and cancelled projects are hidden until asked for, so the list shows what still needs work. */
export const isDone = (x: Pick<FilterableProject, 'status' | 'stage'>) => x.status === 'delivered' || x.status === 'closed' || x.stage.stage === 'cancelled';

export function readFilters(search: string): ProjectFilters {
  const s = new URLSearchParams(search);
  const pick = <T extends string>(k: string, ok: T[]) => (ok.includes(s.get(k) as T) ? (s.get(k) as T) : ('' as T));
  return {
    q: s.get('q') ?? '',
    stage: s.get('stage') ?? '',
    client: s.get('client') ?? '',
    person: s.get('person') ?? '',
    due: pick('due', DUE),
    wait: pick('wait', WAIT),
    src: pick('src', SRC),
    from: month(s.get('from')),
    to: month(s.get('to')),
    done: s.get('done') === '1',
    role: pick('role', ROLES),
    hidden: s.get('hidden') === '1'
  };
}

/** Writes the filters into `search`, keeping unrelated parameters (such as mine) as they are. */
export function writeFilters(search: string, f: ProjectFilters): string {
  const s = new URLSearchParams(search);
  for (const k of KEYS) {
    const v = f[k].trim();
    if (v) s.set(k, v);
    else s.delete(k);
  }
  if (f.done) s.set('done', '1');
  else s.delete('done');
  if (f.hidden) s.set('hidden', '1');
  else s.delete('hidden');
  const out = s.toString();
  return out ? `?${out}` : '';
}

/** How many filters narrow the list (Show delivered widens it, so it does not count). */
export const activeCount = (f: ProjectFilters) => KEYS.filter((k) => f[k].trim()).length + (f.hidden ? 1 : 0);

/** Filters set only from Advanced filters (not search or a one-click view), so the panel opens when a link carries them. */
export const advancedCount = (f: ProjectFilters) =>
  (['stage', 'client', 'person', 'due', 'wait', 'src', 'from', 'to'] as const).filter((k) => f[k]).length - (VIEWS.some((v) => viewOn(f, v)) ? 1 : 0);

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function matchesDue(d: string | null, due: DueFilter, today: DateKey): boolean {
  if (!due) return true;
  if (due === 'none') return !d;
  if (!d) return false;
  if (due === 'overdue') return d < today;
  return d >= today && d <= addDays(today, due === '7d' ? 6 : 29);
}

/** I have the role on the project: on its team, or (editing) the editor of one of its videos. */
export const hasMyRole = (x: Pick<FilterableProject, 'myRoleKeys' | 'videoList'>, role: Exclude<MyRole, ''>) =>
  (x.myRoleKeys ?? []).includes(role) || (role === 'editor' && (x.videoList ?? []).some((v) => v.mine));

export function matches(x: FilterableProject, f: ProjectFilters, today: DateKey): boolean {
  // a project hidden from my My work shows only under "Hidden" (and then only those)
  if (Boolean(x.hidden) !== f.hidden) return false;
  if (f.role && !hasMyRole(x, f.role)) return false;
  // asking for delivered or cancelled projects shows them even with Show delivered off
  const wantsDone = f.done || f.stage === 'delivered' || f.stage === 'cancelled';
  if (!wantsDone && isDone(x)) return false;
  if (f.stage && x.stage.stage !== f.stage) return false;
  if (f.client && x.client.id !== f.client) return false;
  if (f.person && !(x.people ?? []).some((p) => p.id === f.person)) return false;
  if (f.due && (isDone(x) || !matchesDue(x.nextDeadline, f.due, today))) return false;
  if (f.src && (f.src === 'monday') !== Boolean(x.fromMonday)) return false;
  // "Din tur" is My work's rule (a planned video only once its date is near); an older API leaves myTurn out
  if (f.wait === 'turn' ? !(x.myTurn ?? x.next.mine) : f.wait === 'me' ? !x.next.mine : f.wait && x.next.waitingOn !== f.wait) return false;
  const words = norm(f.q).split(/\s+/).filter(Boolean);
  if (words.length) {
    const hay = norm([x.name, x.code, x.client.name, x.producer ?? '', x.next.phrase, x.stage.label, ...(x.people ?? []).map((p) => p.name)].join(' '));
    if (!words.every((w) => hay.includes(w))) return false;
  }
  return true;
}

/** The choices each select offers, built from the projects loaded, with how many projects each would show. */
export function filterOptions(rows: FilterableProject[]) {
  const clients = new Map<string, { id: string; name: string; n: number }>();
  const people = new Map<string, { id: string; name: string; n: number }>();
  for (const x of rows) {
    const c = clients.get(x.client.id) ?? { id: x.client.id, name: x.client.name, n: 0 };
    c.n++;
    clients.set(x.client.id, c);
    for (const p of x.people ?? []) {
      const e = people.get(p.id) ?? { id: p.id, name: p.name, n: 0 };
      e.n++;
      people.set(p.id, e);
    }
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'sv');
  return { clients: [...clients.values()].sort(byName), people: [...people.values()].sort(byName) };
}

/** One-click views: each sets one filter and clears the others the views use, so one view is on at a time. */
export const VIEWS: { key: string; label: string; set: Partial<ProjectFilters> }[] = [
  // "Din tur" for every role (owner 2026-11-03: one list), the same rule as My work
  { key: 'turn', label: 'Your turn', set: { wait: 'turn' } },
  { key: 'overdue', label: 'Overdue', set: { due: 'overdue' } },
  { key: 'week', label: 'Within 7 days', set: { due: '7d' } },
  { key: 'planning', label: 'Planning', set: { stage: 'planning' } },
  { key: 'editing', label: 'In editing', set: { stage: 'editing' } },
  { key: 'review', label: 'Internal review', set: { stage: 'internal_review' } },
  { key: 'client', label: 'With client', set: { wait: 'customer' } }
];

/** The filters' labels in the person's language; the English constants above stay the source of the keys. */
const LABELS = texts(
  {
    stages: Object.fromEntries(STAGE_OPTIONS) as Record<string, string>,
    due: DUE_LABEL,
    source: SOURCE_LABEL,
    wait: WAIT_LABEL,
    views: Object.fromEntries(VIEWS.map((v) => [v.key, v.label])) as Record<string, string>
  },
  {
    stages: {
      planning: 'Planering',
      editing: 'Redigering',
      internal_review: 'Intern granskning',
      with_customer: 'Hos kund',
      ready_to_deliver: 'Redo att leverera',
      delivered: 'Levererad',
      cancelled: 'Avbruten'
    },
    due: { overdue: 'Försenade', '7d': 'Närmaste 7 dagarna', '30d': 'Närmaste 30 dagarna', none: 'Ingen deadline' },
    source: { monday: 'Från Monday', hub: 'Skapade i Hubben' },
    wait: { turn: 'Min tur', me: 'Mig', customer: 'Kund', editor: 'Redigerare', cd: 'CD', producer: 'Producent' },
    views: {
      turn: 'Din tur',
      overdue: 'Försenade',
      week: 'Inom 7 dagar',
      planning: 'Planering',
      editing: 'Under redigering',
      review: 'Intern granskning',
      client: 'Hos kund'
    }
  }
);

/** The select options and view labels for `lang` (English by default, so English output stays as it was). */
export function filterLabels(lang: Lang = 'en') {
  const l = LABELS[lang];
  const tr = <K extends string>(rec: Record<K, string>, sv: Record<string, string>) => Object.entries(rec).map(([k, v]): [string, string] => [k, sv[k] ?? (v as string)]);
  return {
    stages: STAGE_OPTIONS.map(([k, v]): [string, string] => [k, l.stages[k] ?? v]),
    due: tr(DUE_LABEL, l.due),
    source: tr(SOURCE_LABEL, l.source),
    wait: tr(WAIT_LABEL, l.wait),
    view: (v: (typeof VIEWS)[number]) => l.views[v.key] ?? v.label
  };
}

const VIEW_KEYS = ['stage', 'due', 'wait'] as const;

/** A view is on when its filter is set and the other filters views use are empty. */
export const viewOn = (f: ProjectFilters, v: (typeof VIEWS)[number]) => VIEW_KEYS.every((k) => f[k] === (v.set[k] ?? ''));

/** Clicking a view turns it on (and the others off), or off again. */
export const toggleView = (f: ProjectFilters, v: (typeof VIEWS)[number]): ProjectFilters => {
  const cleared = { ...f, stage: '', due: '' as DueFilter, wait: '' as WaitFilter };
  return viewOn(f, v) ? cleared : { ...cleared, ...v.set };
};
